/**
 * Gestor de MODELOS DE CALIFICACIÓN de la academia (grupo E).
 *
 * Lista los modelos del sistema (semilla, clonables) + los personalizados de la
 * org (sincronizados por `academy_org`). Crear/clonar/editar/borrar por REST
 * (`/api/academy/:orgId/grading-models`, authz owner/org_admin). El diálogo trae
 * PREVISUALIZACIÓN EN VIVO: metes una nota cruda (%) y ves el valor nativo +
 * aprobado/reprobado (+ letra si aplica). El umbral se introduce en unidades
 * NATIVAS ("aprueba con 10 / 20") y se guarda como fracción 0..1.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  type GradingLetterBand,
  type GradingModelKind,
  type GradingModelShape,
  modelShapeFromRow,
  modelStrategy,
  parseGradingModelKind,
} from "@space/shared";
import type { GradingModel } from "@space/powersync";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { InfoHint } from "@/components/ui/info-hint";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/providers/i18n-provider";
import { useGradingModels } from "../hooks/use-academy-data";
import {
  createGradingModel,
  deleteGradingModel,
  type GradingModelInput,
  updateGradingModel,
} from "../grading/api";

function kindLabel(t: (m: ReturnType<typeof msg>) => string, kind: string): string {
  switch (kind) {
    case "points": {
      return t(msg`Points`);
    }
    case "percent": {
      return t(msg`Percentage`);
    }
    case "letter": {
      return t(msg`Letters`);
    }
    case "pass_fail": {
      return t(msg`Pass / Fail`);
    }
    default: {
      return kind;
    }
  }
}

interface DialogState {
  editingId: string | null;
  cloneFrom: string | null;
  name: string;
  kind: GradingModelKind;
  maxValue: number;
  thresholdNative: number;
  decimals: number;
  suffix: string;
  bands: GradingLetterBand[];
  sample: number; // nota cruda 0..100 para la previsualización
}

function emptyBands(): GradingLetterBand[] {
  return [
    { label: "A", minPct: 90 },
    { label: "B", minPct: 80 },
    { label: "C", minPct: 70 },
    { label: "D", minPct: 60 },
    { label: "F", minPct: 0 },
  ];
}

function shapeFromRow(row: GradingModel): GradingModelShape {
  return modelShapeFromRow({
    id: row.id,
    kind: row.kind ?? "points",
    max_value: row.max_value ?? 0,
    passing_threshold: row.passing_threshold ?? 0,
    decimals: row.decimals ?? 0,
    letter_bands: row.letter_bands,
    display_suffix: row.display_suffix,
    is_system: Boolean(row.is_system),
    system_key: row.system_key ?? null,
  });
}

export function GradingModelsManager(props: { orgId: string; canManage: boolean }) {
  const { t } = useI18n();
  const models = useGradingModels(() => props.orgId);
  const [dialog, setDialog] = createSignal<DialogState | null>(null);
  const [saving, setSaving] = createSignal(false);

  const openNew = () => {
    setDialog({
      editingId: null,
      cloneFrom: null,
      name: "",
      kind: "points",
      maxValue: 20,
      thresholdNative: 10,
      decimals: 1,
      suffix: "",
      bands: emptyBands(),
      sample: 70,
    });
  };

  const openFrom = (row: GradingModel, mode: "edit" | "clone") => {
    const shape = shapeFromRow(row);
    setDialog({
      editingId: mode === "edit" ? row.id : null,
      cloneFrom: mode === "clone" ? row.id : null,
      name: mode === "clone" ? `${row.name ?? ""} (copy)` : (row.name ?? ""),
      kind: shape.kind,
      maxValue: shape.maxValue,
      thresholdNative: round2(shape.passingThreshold * shape.maxValue),
      decimals: shape.decimals,
      suffix: shape.displaySuffix ?? "",
      bands:
        shape.letterBands && shape.letterBands.length > 0 ? [...shape.letterBands] : emptyBands(),
      sample: 70,
    });
  };

  const patch = (p: Partial<DialogState>) => {
    const cur = dialog();
    if (cur) {
      setDialog({ ...cur, ...p });
    }
  };

  // Forma actual del diálogo → estrategia para la previsualización en vivo.
  const previewShape = createMemo<GradingModelShape | null>(() => {
    const d = dialog();
    if (!d) {
      return null;
    }
    const max = d.maxValue > 0 ? d.maxValue : 1;
    return {
      schemeKey: "preview",
      kind: d.kind,
      maxValue: max,
      passingThreshold: Math.min(1, Math.max(0.0001, d.thresholdNative / max)),
      decimals: d.decimals,
      letterBands: d.kind === "letter" ? d.bands : null,
      displaySuffix: d.suffix || null,
    };
  });

  const preview = createMemo(() => {
    const shape = previewShape();
    const d = dialog();
    if (!shape || !d) {
      return null;
    }
    const raw = Math.min(1, Math.max(0, d.sample / 100));
    const strat = modelStrategy(shape);
    return { display: strat.toDisplay(raw), passing: strat.isPassing(raw) };
  });

  const save = async () => {
    const d = dialog();
    if (!d || saving()) {
      return;
    }
    const max = d.maxValue > 0 ? d.maxValue : 0;
    const input: GradingModelInput = {
      name: d.name.trim(),
      kind: d.kind,
      max_value: max,
      passing_threshold: max > 0 ? Math.min(1, Math.max(0.0001, d.thresholdNative / max)) : 0,
      decimals: d.decimals,
      display_suffix: d.suffix.trim() || null,
      letter_bands: d.kind === "letter" ? d.bands : null,
    };
    setSaving(true);
    try {
      if (d.editingId) {
        await updateGradingModel(props.orgId, d.editingId, input);
        showToast(t(msg`Grading model saved`), "success");
      } else {
        if (d.cloneFrom) {
          input.clone_from = d.cloneFrom;
        }
        await createGradingModel(props.orgId, input);
        showToast(t(msg`Grading model created`), "success");
      }
      setDialog(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t(msg`Could not save the model`), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: GradingModel) => {
    if (!props.canManage) {
      return;
    }
    try {
      await deleteGradingModel(props.orgId, row.id);
      showToast(t(msg`Grading model deleted`), "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : t(msg`Could not delete the model`), "error");
    }
  };

  const summary = (row: GradingModel): string => {
    const shape = shapeFromRow(row);
    const strat = modelStrategy(shape);
    const kind = kindLabel(t, shape.kind);
    const thr = strat.displayValue(shape.passingThreshold * shape.maxValue);
    const max = strat.displayValue(shape.maxValue);
    return t(msg`${kind} · passes at ${thr} of ${max}`);
  };

  return (
    <Card>
      <CardHeader class="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle class="text-lg font-bold">
            {t(msg`Grading models`)}{" "}
            <InfoHint>
              {t(
                msg`A grading model is a reusable scale: its maximum value, how a grade is displayed, and the mark needed to pass (e.g. "passes with 10 / 20"). The 4 system models can be cloned to create your own; courses and cohorts pick one.`,
              )}
            </InfoHint>
          </CardTitle>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Reusable grading scales for your academy. System models are read-only — clone one to customize the passing mark or the letter bands.`,
            )}
          </p>
        </div>
        <Show when={props.canManage}>
          <Button onClick={openNew} data-testid="grading-model-new">
            {t(msg`New model`)}
          </Button>
        </Show>
      </CardHeader>
      <CardContent>
        <Show when={!models.isLoading} fallback={<Skeleton class="h-40 w-full rounded-lg" />}>
          <ul class="space-y-2" data-testid="grading-models-list">
            <For each={models.data ?? []}>
              {(row) => (
                <li
                  class="flex items-center justify-between gap-3 rounded-lg border border-border/50 px-3 py-2"
                  data-testid={`grading-model-row-${row.id}`}
                  data-model-name={row.name ?? ""}
                >
                  <div class="min-w-0">
                    <p class="flex items-center gap-2 truncate text-sm font-semibold">
                      {row.name}
                      <Show when={row.is_system}>
                        <span class="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                          {t(msg`System`)}
                        </span>
                      </Show>
                    </p>
                    <p class="truncate text-xs text-muted-foreground">{summary(row)}</p>
                  </div>
                  <Show when={props.canManage}>
                    <div class="flex shrink-0 gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openFrom(row, "clone")}
                        data-testid={`grading-model-clone-${row.id}`}
                      >
                        {t(msg`Clone`)}
                      </Button>
                      <Show when={!row.is_system}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openFrom(row, "edit")}
                          data-testid={`grading-model-edit-${row.id}`}
                        >
                          {t(msg`Edit`)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(row)}
                          data-testid={`grading-model-delete-${row.id}`}
                        >
                          {t(msg`Delete`)}
                        </Button>
                      </Show>
                    </div>
                  </Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </CardContent>

      <Dialog open={dialog() != null} onOpenChange={(o) => !o && setDialog(null)}>
        <Show when={dialog()}>
          {(d) => (
            <div class="space-y-4" data-testid="grading-model-dialog">
              <DialogHeader>
                <DialogTitle>
                  {d().editingId ? t(msg`Edit grading model`) : t(msg`New grading model`)}
                </DialogTitle>
              </DialogHeader>

              <label class="block space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Name`)}</span>
                <TextField
                  value={d().name}
                  data-testid="grading-model-name"
                  onInput={(e) => patch({ name: e.currentTarget.value })}
                />
              </label>

              <div class="grid gap-3 sm:grid-cols-2">
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Type`)}</span>
                  <Select
                    aria-label={t(msg`Type`)}
                    value={d().kind}
                    onChange={(v) => patch({ kind: parseGradingModelKind(v) })}
                    options={["points", "percent", "letter", "pass_fail"].map((k) => ({
                      value: k,
                      label: kindLabel(t, k),
                    }))}
                  />
                </label>
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Maximum value`)}</span>
                  <TextField
                    type="number"
                    inputmode="decimal"
                    value={String(d().maxValue)}
                    data-testid="grading-model-max"
                    onInput={(e) => patch({ maxValue: Number(e.currentTarget.value) || 0 })}
                  />
                </label>
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">
                    {t(msg`Passes with`)}{" "}
                    <InfoHint>
                      {t(msg`The mark needed to pass, in native units (e.g. 10 out of 20).`)}
                    </InfoHint>
                  </span>
                  <div class="flex items-center gap-2">
                    <TextField
                      type="number"
                      inputmode="decimal"
                      value={String(d().thresholdNative)}
                      data-testid="grading-model-threshold"
                      onInput={(e) =>
                        patch({ thresholdNative: Number(e.currentTarget.value) || 0 })
                      }
                    />
                    <span class="whitespace-nowrap text-sm text-muted-foreground">
                      / {d().maxValue}
                    </span>
                  </div>
                </label>
                <Show when={d().kind === "points" || d().kind === "percent"}>
                  <label class="space-y-1.5">
                    <span class="text-sm font-medium">{t(msg`Decimals`)}</span>
                    <TextField
                      type="number"
                      inputmode="numeric"
                      value={String(d().decimals)}
                      onInput={(e) => patch({ decimals: Number(e.currentTarget.value) || 0 })}
                    />
                  </label>
                </Show>
                <Show when={d().kind === "points" || d().kind === "percent"}>
                  <label class="space-y-1.5">
                    <span class="text-sm font-medium">{t(msg`Display suffix`)}</span>
                    <TextField
                      value={d().suffix}
                      placeholder={t(msg`e.g. %`)}
                      onInput={(e) => patch({ suffix: e.currentTarget.value })}
                    />
                  </label>
                </Show>
              </div>

              {/* Editor de bandas de letra */}
              <Show when={d().kind === "letter"}>
                <div class="space-y-2 rounded-lg border border-border/60 p-3">
                  <p class="text-sm font-medium">{t(msg`Letter bands`)}</p>
                  <p class="text-xs text-muted-foreground">
                    {t(
                      msg`Each band gives a label from a minimum %, descending. The last one must be 0%.`,
                    )}
                  </p>
                  <For each={d().bands}>
                    {(band, i) => (
                      <div class="flex items-center gap-2">
                        <TextField
                          value={band.label}
                          aria-label={t(msg`Label`)}
                          onInput={(e) => {
                            const bands = d().bands.map((b, j) =>
                              j === i() ? { ...b, label: e.currentTarget.value } : b,
                            );
                            patch({ bands });
                          }}
                        />
                        <TextField
                          type="number"
                          inputmode="numeric"
                          aria-label={t(msg`Minimum %`)}
                          value={String(band.minPct)}
                          onInput={(e) => {
                            const bands = d().bands.map((b, j) =>
                              j === i() ? { ...b, minPct: Number(e.currentTarget.value) || 0 } : b,
                            );
                            patch({ bands });
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => patch({ bands: d().bands.filter((_, j) => j !== i()) })}
                        >
                          {t(msg`Remove`)}
                        </Button>
                      </div>
                    )}
                  </For>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => patch({ bands: [...d().bands, { label: "", minPct: 0 }] })}
                  >
                    {t(msg`Add band`)}
                  </Button>
                </div>
              </Show>

              {/* Previsualización en vivo */}
              <div class="space-y-2 rounded-lg bg-muted/40 p-3" data-testid="grading-model-preview">
                <p class="text-sm font-medium">{t(msg`Live preview`)}</p>
                <label class="flex items-center gap-3 text-sm">
                  <span class="whitespace-nowrap text-muted-foreground">
                    {t(msg`Sample raw score`)}
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={d().sample}
                    class="flex-1"
                    onInput={(e) => patch({ sample: Number(e.currentTarget.value) })}
                  />
                  <span class="w-10 text-right tabular-nums">{d().sample}%</span>
                </label>
                <Show when={preview()}>
                  {(pv) => (
                    <p class="text-sm">
                      {t(msg`Shows as`)}{" "}
                      <strong data-testid="grading-model-preview-value">{pv().display}</strong> —{" "}
                      <span
                        class={
                          pv().passing
                            ? "font-semibold text-emerald-600"
                            : "font-semibold text-red-600"
                        }
                        data-testid="grading-model-preview-passing"
                      >
                        {pv().passing ? t(msg`Passes`) : t(msg`Fails`)}
                      </span>
                    </p>
                  )}
                </Show>
              </div>

              <div class="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialog(null)}>
                  {t(msg`Cancel`)}
                </Button>
                <Button onClick={save} disabled={saving()} data-testid="grading-model-save">
                  <Show when={saving()} fallback={t(msg`Save`)}>
                    {t(msg`Saving...`)}
                  </Show>
                </Button>
              </div>
            </div>
          )}
        </Show>
      </Dialog>
    </Card>
  );
}

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}
